import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { Link } from 'react-router';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import DatePicker from '../../components/DatePicker';
import moment from 'moment';

interface SignUpUserDetailsProps {
  otherClasses?: string;
  signUpHandler: any;
  handleClick?: () => void;
}

const SignUpUserDetails: React.FC<SignUpUserDetailsProps> = ({
  otherClasses,
  signUpHandler,
  handleClick,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const signUpUserDetailsClasses = clsx(
    otherClasses,
    'w-full flex flex-col items-center flex flex-col gap-5 md:gap-30 lg:gap-10',
  );
  const sizes = useButtonSizeLarge();

  const handleClickWrapper = async () => {
    setIsLoading(true);
    try {
      if (handleClick) {
        await handleClick();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={signUpUserDetailsClasses}
      data-testid="sign-up-user-details"
    >
      <div className="grid w-full grid-cols-1 gap-5 md:gap-30 lg:grid-cols-2 lg:gap-10">
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Full Name*"
          errorMessage={
            signUpHandler?.touched.fullName && signUpHandler?.errors.fullName
              ? signUpHandler?.errors.fullName
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.fullName}
          type="text"
          placeholder="e.g. Johnny Appleseed"
          onChange={signUpHandler.handleChange}
          name="fullName"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.fullName && !!signUpHandler?.errors.fullName
          }
          aria-describedby={
            signUpHandler?.touched.fullName && signUpHandler?.errors.fullName
              ? 'fullName-error'
              : undefined
          }
        />
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Email*"
          errorMessage={
            signUpHandler?.touched.email && signUpHandler?.errors.email
              ? signUpHandler?.errors.email
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.email}
          type="email"
          placeholder="someone@gmail.com"
          onChange={signUpHandler.handleChange}
          name="email"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.email && !!signUpHandler?.errors.email
          }
          aria-describedby={
            signUpHandler?.touched.email && signUpHandler?.errors.email
              ? 'email-error'
              : undefined
          }
        />
      </div>
      <div className="grid w-full grid-cols-1 gap-5 md:gap-30 lg:grid-cols-2 lg:gap-10">
        <Input
          onBlur={signUpHandler.handleBlur}
          label={
            <>
              Contact Number <em className="text-sm font-normal">(Optional)</em>
            </>
          }
          errorMessage={
            signUpHandler?.touched.contact && signUpHandler?.errors.contact
              ? signUpHandler?.errors.contact
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.contact}
          type="tel"
          placeholder="Enter contact number"
          onChange={signUpHandler.handleChange}
          name="contact"
          otherClasses={{ container: 'w-full' }}
        />
        <DatePicker
          otherClasses={{ container: 'w-full' }}
          label={
            <>
              Date of Birth <em className="text-sm font-normal">(Optional)</em>
            </>
          }
          name="date"
          startMonth={moment([1950, 0]).toDate()}
          endMonth={moment().toDate()}
          // endMonth={moment().subtract(18, 'years').toDate()}
          value={signUpHandler?.values?.dob}
          placeholder="Select date of birth"
          // onBlur={signUpHandler.handleBlur}
          onBlur={() => {
            if (signUpHandler?.values?.dob) {
              signUpHandler?.setFieldTouched('dob', true);
            }
          }}
          disabled={signUpHandler.isSubmitting}
          onChange={(e) => {
            signUpHandler?.setFieldValue('dob', e, true);
          }}
          errorMessage={
            signUpHandler?.touched.dob && signUpHandler?.errors.dob
              ? signUpHandler?.errors.dob
              : ''
          }
        />
      </div>
      <div className="grid w-full grid-cols-1 gap-5 md:gap-30 lg:grid-cols-2 lg:gap-10">
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Password*"
          errorMessage={
            signUpHandler?.touched.password && signUpHandler?.errors.password
              ? signUpHandler?.errors.password
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.password}
          type="password"
          placeholder="Type password"
          onChange={signUpHandler.handleChange}
          name="password"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.password && !!signUpHandler?.errors.password
          }
          aria-describedby={
            signUpHandler?.touched.password && signUpHandler?.errors.password
              ? 'password-error'
              : undefined
          }
        />
        <Input
          onBlur={signUpHandler.handleBlur}
          label="Confirm Password*"
          errorMessage={
            signUpHandler?.touched.confirmPassword &&
            signUpHandler?.errors.confirmPassword
              ? signUpHandler?.errors.confirmPassword
              : ''
          }
          disabled={signUpHandler.isSubmitting}
          value={signUpHandler.values.confirmPassword}
          type="password"
          placeholder="Retype password"
          onChange={signUpHandler.handleChange}
          name="confirmPassword"
          otherClasses={{ container: 'w-full' }}
          aria-required={true}
          aria-invalid={
            signUpHandler?.touched.confirmPassword &&
            !!signUpHandler?.errors.confirmPassword
          }
          aria-describedby={
            signUpHandler?.touched.confirmPassword &&
            signUpHandler?.errors.confirmPassword
              ? 'confirmPassword-error'
              : undefined
          }
        />
      </div>

      <Button
        size={sizes}
        disable={isLoading}
        loading={isLoading}
        otherClasses="w-full max-w-465 mt-2 sm:mt-0"
        type="button"
        onClick={handleClickWrapper}
        aria-label="Proceed to sign up"
      >
        Proceed
      </Button>
      <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-medium md:text-lg lg:text-xl lg:leading-6">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          className="text-brand-byzantine"
          aria-label="Sign in to your account"
        >
          Sign In
        </Link>{' '}
      </p>
    </div>
  );
};

export default SignUpUserDetails;
